/**
 * Specialist appointment requests (M14-T05/T08). A request, not a paid booking
 * (ADR-0029) — no payment anywhere. Create + list + cancel need a verified
 * patient session `token`; available-dates is a public read.
 */
import { apiFetch } from '../lib/api'

/** A concrete date the patient may pick, tagged by which week of the two-week
 * window it falls in (1 = next 7 days, 2 = the following 7). */
export type AvailableDate = { date: string; week: number }

/** GET /api/v1/specialists/{slug}/available-dates?hospital=&session= — the pickable
 * in-window dates for one clinic session. Empty (not an error) for an unknown
 * session → the caller shows the AMTC call-centre fallback (ADR-0030). */
export async function fetchAvailableDates(
  specialistSlug: string,
  hospitalSlug: string,
  sessionId: string,
  signal?: AbortSignal,
): Promise<AvailableDate[]> {
  const qs = new URLSearchParams({ hospital: hospitalSlug, session: sessionId })
  const opts = signal ? { signal } : {}
  const body = await apiFetch<{ data: AvailableDate[] }>(
    `/api/v1/specialists/${specialistSlug}/available-dates?${qs.toString()}`,
    opts,
  )
  return body.data
}

export type AppointmentRequestInput = {
  specialistSlug: string
  hospitalSlug: string
  clinicSessionId: string
  requestedDate: string
  reasonForVisit: string
  currentMedications?: string
  insuranceProvider?: string
  area?: string
}

export type AppointmentRequestCreated = { reference: string; status: string }

/** POST /api/v1/patient/appointment-requests — create a request. Needs a verified
 * patient session `token`. */
export async function submitAppointmentRequest(
  token: string,
  input: AppointmentRequestInput,
): Promise<AppointmentRequestCreated> {
  const body = await apiFetch<{ data: AppointmentRequestCreated }>(
    '/api/v1/patient/appointment-requests',
    {
      method: 'POST',
      token,
      body: {
        specialist_slug: input.specialistSlug,
        hospital_slug: input.hospitalSlug,
        clinic_session_id: input.clinicSessionId,
        requested_date: input.requestedDate,
        reason_for_visit: input.reasonForVisit,
        ...(input.currentMedications
          ? { current_medications: input.currentMedications }
          : {}),
        ...(input.insuranceProvider
          ? { insurance_provider: input.insuranceProvider }
          : {}),
        ...(input.area ? { area: input.area } : {}),
      },
    },
  )
  return body.data
}

export type MyAppointmentSession = {
  day_of_week: number
  part_of_day: string
  start_time?: string | null
  end_time?: string | null
}

export type MyAppointment = {
  reference: string
  specialist_name: string
  specialist_slug: string
  specialty: string
  hospital_name: string
  hospital_slug: string
  session: MyAppointmentSession
  requested_date: string
  consult_fee_pesewas: number | null
  reason_for_visit: string
  status: string
  confirmed_for?: string | null
  decline_reason?: string | null
  created_at: string
}

/** GET /api/v1/patient/appointment-requests — the caller's own requests. */
export async function fetchMyAppointments(
  token: string,
  signal?: AbortSignal,
): Promise<MyAppointment[]> {
  const opts = signal ? { token, signal } : { token }
  const body = await apiFetch<{ data: MyAppointment[] }>(
    '/api/v1/patient/appointment-requests',
    opts,
  )
  return body.data
}

/** POST /api/v1/patient/appointment-requests/{ref}/cancel */
export async function cancelAppointment(
  token: string,
  reference: string,
): Promise<AppointmentRequestCreated> {
  const body = await apiFetch<{ data: AppointmentRequestCreated }>(
    `/api/v1/patient/appointment-requests/${reference}/cancel`,
    { method: 'POST', token },
  )
  return body.data
}
